import { CompanyFiltersState } from "@/lib/types/company.types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { logout } from "./authSlice";

import { CompaniesExtendedState } from "@/lib/types/company.types";

const initialCompanyFilters: CompanyFiltersState = {
  companyNameWebsite: '',
  industryType: '',
  industryKeyword: '',
  selectedIndustries: [],
  selectedSubIndustries: [],
  selectedSicCodes: [],
  selectedCompanySizes: [],
  revenue: [],
  companyLocation: '',
  productService: [],
  technologyParameter: [],
  suppressions: '',
  excludeAddedCompanies: '',
  selectedCompanies: [],
  hqCountry: [],
  hqState: [],
  hqCity: [],
  hqPostalCode: [],
  hqCounty: [],
  intentCategories: [],
  intentTopics: [],
  intentScore: [],
  includeOffices: false,
  groups: [],
  excludeExportedCompanies: false,
  CompaniesByAddDateStart: '',
  CompaniesByAddDateEnd: '',
  ExportedCompaniesStart: '',
  ExportedCompaniesEnd: '',
  foundedMinYear: null,
  foundedMaxYear: null,
  directDialOptions: {
    emailable: false,
    phoneable: false,
    contactEmailable: false,
    contactPhoneable: false,
  },
};

const initialState: CompaniesExtendedState & { companyFilters: CompanyFiltersState } = {
  /**
   * Never written today: no reducer dispatches setSelectedCompanyFilter, so this
   * stays null. It IS read - useCompaniesSearch's hasNameFilter and
   * buildFiltersObject's companyName fallback both branch on it - so the setter
   * is kept for whenever that filter gets wired up.
   */
  selectedCompanyFilter: null,
  selectedKeywords: [],
  includeOffices: false, // Disabled (and off) initially until a location is selected
  companyFilters: initialCompanyFilters,
};

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    setSelectedCompanyFilter: (
      state,
      action: PayloadAction<{
        name: string;
        companyId: string;
        website: string;
        exclude: boolean;
      } | null>
    ) => {
      state.selectedCompanyFilter = action.payload;
    },
    clearSelectedCompanyFilter: (state) => {
      state.selectedCompanyFilter = null;
    },
    addSelectedKeyword: (state, action: PayloadAction<{ tag: string; exclude: boolean; }>) => {
      // Don't add if already exists
      const exists = state.selectedKeywords.find(k => k.tag === action.payload.tag);
      if (!exists) {
        state.selectedKeywords.push(action.payload);
      }
    },
    removeSelectedKeyword: (state, action: PayloadAction<string>) => {
      state.selectedKeywords = state.selectedKeywords.filter(k => k.tag !== action.payload);
    },
    setSelectedKeywords: (state, action: PayloadAction<typeof state.selectedKeywords>) => {
      state.selectedKeywords = action.payload;
    },
    toggleKeywordExclude: (state, action: PayloadAction<string>) => {
      const keyword = state.selectedKeywords.find(k => k.tag === action.payload);
      if (keyword) {
        keyword.exclude = !keyword.exclude;
      }
    },
    clearSelectedKeywords: (state) => {
      state.selectedKeywords = [];
    },
    setIncludeOffices: (state, action: PayloadAction<boolean>) => {
      state.includeOffices = action.payload;
    },
    setCompanyFilterField: <K extends keyof CompanyFiltersState>(state: typeof initialState, action: PayloadAction<{ field: K; value: CompanyFiltersState[K] }>) => {
      state.companyFilters[action.payload.field] = action.payload.value;
    },
    updateCompanyFilters: (state: typeof initialState, action: PayloadAction<Partial<CompanyFiltersState>>) => {
      state.companyFilters = { ...state.companyFilters, ...action.payload };
    },
    resetCompanyFilters: (state: typeof initialState) => {
      state.companyFilters = { ...initialCompanyFilters };
      // Also clear related filter states
      state.selectedCompanyFilter = null;
      state.selectedKeywords = [];
      // Mirrors companyFilters.includeOffices for the LocationFilter switch's
      // own useSelector - reset it too, or the switch would keep showing a
      // stale value after Clear All.
      state.includeOffices = false;
    },
  },
  extraReducers: (builder) => {
    // Client-side routing means the Redux store survives a logout - login
    // cycle (unlike a full page refresh, which cleanSession()'s localStorage
    // clear alone already covers). Without this, Company search filters from
    // the previous session stayed in memory and reappeared for whoever logs
    // in next.
    builder.addCase(logout.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setSelectedCompanyFilter,
  clearSelectedCompanyFilter,
  addSelectedKeyword,
  removeSelectedKeyword,
  setSelectedKeywords,
  toggleKeywordExclude,
  clearSelectedKeywords,
  setIncludeOffices,
  setCompanyFilterField,
  updateCompanyFilters,
  resetCompanyFilters,
} = companiesSlice.actions;
export default companiesSlice.reducer;
